"use client";

import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useFormatter, useTranslations } from "next-intl";

import * as Dialog from "@/components/dialog/DialogLayout";
import { FormFieldError } from "@/components/form/FormField";
import { Spinner } from "@/components/spinner/Spinner";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import { PlanCard } from "./PlanCard";

import type { SubscriptionPlan } from "@/api";

const FREE_FEATURES = [
  "upgradePlanDialog.plans.free.features.noCreditCard",
  "upgradePlanDialog.plans.free.features.unlimitedDocuments",
  "upgradePlanDialog.plans.free.features.exports",
  "upgradePlanDialog.plans.free.features.imageRecognition",
  "upgradePlanDialog.plans.free.features.credits",
  "upgradePlanDialog.plans.free.features.publishing",
] as const;

const ENTERPRISE_FEATURES = [
  "upgradePlanDialog.plans.enterprise.features.payAsYouGo",
  "upgradePlanDialog.plans.enterprise.features.spendLimits",
  "upgradePlanDialog.plans.enterprise.features.roleBasedAccess",
  "upgradePlanDialog.plans.enterprise.features.scim",
  "upgradePlanDialog.plans.enterprise.features.networkAccessControl",
  "upgradePlanDialog.plans.enterprise.features.auditLogs",
] as const;

const PAID_PLANS = {
  personal: {
    description: "upgradePlanDialog.plans.personal.description",
    priceSuffix: "upgradePlanDialog.perMonth",
    action: "upgradePlanDialog.plans.personal.action",
    featuresTitle: "upgradePlanDialog.plans.personal.featuresTitle",
    features: [
      "upgradePlanDialog.plans.personal.features.credits",
      "upgradePlanDialog.plans.personal.features.frontierModels",
      "upgradePlanDialog.plans.personal.features.exports",
      "upgradePlanDialog.plans.personal.features.codeExecution",
      "upgradePlanDialog.plans.personal.features.imageRecognition",
    ],
  },
  team: {
    description: "upgradePlanDialog.plans.team.description",
    priceSuffix: "upgradePlanDialog.perUserPerMonth",
    action: "upgradePlanDialog.plans.team.action",
    featuresTitle: "upgradePlanDialog.plans.team.featuresTitle",
    features: [
      "upgradePlanDialog.plans.team.features.credits",
      "upgradePlanDialog.plans.team.features.usageCredits",
      "upgradePlanDialog.plans.team.features.sso",
      "upgradePlanDialog.plans.team.features.permissions",
      "upgradePlanDialog.plans.team.features.privateProjects",
    ],
  },
} as const;

const CONTACT_SALES_URL = "mailto:sales@corca.io";

const PaidPlanCard = observer(function PaidPlanCard({ plan }: { plan: SubscriptionPlan }) {
  const t = useTranslations();
  const format = useFormatter();
  const { upgradePlanDialog } = useStore().dialog;
  const texts = PAID_PLANS[plan.kind];
  const endsAt = upgradePlanDialog.subscription?.endsAt;

  return (
    <PlanCard
      name={plan.name}
      description={t(texts.description)}
      cents={plan.priceCents}
      priceSuffix={t(texts.priceSuffix)}
      isCurrent={upgradePlanDialog.isSubscribed}
      currentLabel={
        endsAt
          ? t("upgradePlanDialog.currentPlanUntil", {
              date: format.dateTime(new Date(endsAt), { month: "short", day: "numeric" }),
            })
          : undefined
      }
      action={{
        label: t(texts.action),
        onClick: () => upgradePlanDialog.checkout(plan),
        isBusy: plan.uid === upgradePlanDialog.checkingOutPlanUid,
      }}
      featuresTitle={t(texts.featuresTitle)}
      features={texts.features.map((key) => t(key, { credits: plan.aiCredits }))}
    />
  );
});

export const UpgradePlanDialog = observer(function UpgradePlanDialog() {
  const t = useTranslations();
  const { auth, dialog } = useStore();
  const { upgradePlanDialog } = dialog;
  const {
    isOpen,
    status,
    availablePlan,
    isPersonalTeam,
    isSubscribed,
    isCanceled,
    isDowngrading,
    error,
  } = upgradePlanDialog;

  useEffect(() => {
    if (!isOpen) return;
    void upgradePlanDialog.load();
  }, [auth.isLoggedIn, isOpen, upgradePlanDialog]);

  return (
    <Dialog.Root open={isOpen} onClose={() => upgradePlanDialog.close()}>
      <Dialog.Backdrop />
      <Dialog.Container>
        <Dialog.Panel
          className={twMerge(
            "w-full max-w-[880px] mx-2 p-2.5 gap-2.5 overflow-y-auto",
            "sm:max-w-none sm:h-full sm:mx-0 sm:rounded-none",
          )}
        >
          <Dialog.Title
            className={twMerge(
              "h-7.5 flex items-center shrink-0",
              "text-[12px] font-mono font-normal tracking-tight uppercase",
            )}
          >
            {t("upgradePlanDialog.title")}
          </Dialog.Title>

          {error && <FormFieldError message={error} />}

          {status === "loading" && (
            <div className="flex items-center justify-center h-99">
              <Spinner />
            </div>
          )}

          {status === "ready" && (
            <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-1">
              <PlanCard
                name={t("upgradePlanDialog.plans.free.name")}
                description={t("upgradePlanDialog.plans.free.description")}
                cents={0}
                priceSuffix={t("upgradePlanDialog.perMonth")}
                isCurrent={!isSubscribed}
                action={{
                  label: t("upgradePlanDialog.plans.free.action"),
                  onClick: () => upgradePlanDialog.downgrade(),
                  isBusy: isDowngrading,
                  isDisabled: isCanceled,
                  isMuted: true,
                }}
                featuresTitle={t("upgradePlanDialog.plans.free.featuresTitle")}
                features={FREE_FEATURES.map((key) => t(key))}
              />

              {availablePlan && <PaidPlanCard plan={availablePlan} />}

              <PlanCard
                name={t("upgradePlanDialog.plans.enterprise.name")}
                description={t("upgradePlanDialog.plans.enterprise.description")}
                isCurrent={false}
                action={{
                  label: t("upgradePlanDialog.plans.enterprise.action"),
                  href: CONTACT_SALES_URL,
                }}
                featuresTitle={t(
                  isPersonalTeam
                    ? "upgradePlanDialog.plans.enterprise.featuresTitleAfterPersonal"
                    : "upgradePlanDialog.plans.enterprise.featuresTitleAfterTeam",
                )}
                features={ENTERPRISE_FEATURES.map((key) => t(key))}
              />
            </div>
          )}

          <Dialog.CloseButton>
            <Dialog.CloseIcon />
          </Dialog.CloseButton>
        </Dialog.Panel>
      </Dialog.Container>
    </Dialog.Root>
  );
});
